import { toBidReceiptAccount } from '../accounts.mjs';
import { BidReceiptGpaBuilder } from '../gpaBuilders/BidReceiptGpaBuilder.mjs';
import { useOperation } from '../../../types/Operation.mjs';
import { toLazyBid } from '../models/Bid.mjs';

// -----------------
// Operation
// -----------------

const Key = 'FindBidsOperation';

/**
 * Finds Bids by multiple criteria.
 * You can use any combination of keys.
 *
 * ```ts
 * // Find all bids in an Auction House.
 * const bids = await metaplex
 *   .auctionHouse()
 *   .findBids({ auctionHouse });
 *
 * // Find bids by buyer and mint.
 * const bids = await metaplex
 *   .auctionHouse()
 *   .findBids({ auctionHouse, buyer, mint });
 *
 * // Find bids by metadata.
 * const bids = await metaplex
 *   .auctionHouse()
 *   .findBids({ auctionHouse, metadata });
 * ```
 *
 * @group Operations
 * @category Constructors
 */
const findBidsOperation = useOperation(Key);

/**
 * @group Operations
 * @category Types
 */

/**
 * @group Operations
 * @category Handlers
 */
const findBidsOperationHandler = {
  handle: async (operation, metaplex, scope) => {
    const {
      programs,
      commitment
    } = scope;
    const {
      auctionHouse,
      buyer,
      metadata,
      mint
    } = operation.input;
    const auctionHouseProgram = metaplex.programs().getAuctionHouse(programs);
    let bidQuery = new BidReceiptGpaBuilder(metaplex, auctionHouseProgram.address).mergeConfig({
      commitment
    }).whereAuctionHouse(auctionHouse.address);
    if (buyer) {
      bidQuery = bidQuery.whereBuyer(buyer);
    }
    if (metadata && !mint) {
      bidQuery = bidQuery.whereMetadata(metadata);
    }
    if (mint) {
      bidQuery = bidQuery.whereMetadata(metaplex.nfts().pdas().metadata({
        mint,
        programs
      }));
    }
    scope.throwIfCanceled();
    return bidQuery.getAndMap(account => toLazyBid(toBidReceiptAccount(account), auctionHouse));
  }
};

export { findBidsOperation, findBidsOperationHandler };
//# sourceMappingURL=findBids.mjs.map
